import React, { useState, useEffect } from 'react';
import './EndpointDetectionResponse.css';
import {
  Shield,
  Monitor,
  Activity,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Clock,
  Zap,
  Globe,
  RefreshCw
} from 'lucide-react';
import { edrService } from '../services/edrService';

function EndpointDetectionResponse() {
  const [endpoints, setEndpoints] = useState([]);
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemHealth, setSystemHealth] = useState({
    cpu: 0,
    memory: 0,
    disk: 0,
    network: 0
  });
  const [threatLevel, setThreatLevel] = useState('low');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [endpointsMonitored, setEndpointsMonitored] = useState(0);
  const [activeThreats, setActiveThreats] = useState(0);
  const [clearing, setClearing] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [selectedOS, setSelectedOS] = useState(null);
  const [registrationCode, setRegistrationCode] = useState(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const fetchEDRData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to view EDR dashboard');
      }

      // Fetch endpoints, threats, and status from real backend using service
      const [endpointsData, threatsData, statusData] = await Promise.all([
        edrService.getEndpoints(),
        edrService.getThreats({ limit: 100 }),
        edrService.getStatus()
      ]);

      // Set real endpoints data
      setEndpoints(endpointsData || []);
      setEndpointsMonitored(endpointsData?.length || 0);

      // Show onboarding modal if no endpoints AND user hasn't dismissed it this session
      if (!endpointsData || endpointsData.length === 0) {
        const dismissedThisSession = sessionStorage.getItem('edr_onboarding_dismissed');
        console.log('📊 EDR Check: No endpoints found. Dismissed this session?', dismissedThisSession);
        if (!dismissedThisSession) {
          console.log('✅ Showing onboarding modal');
          setShowOnboardingModal(true);
          // Generate registration code
          if (!registrationCode) {
            generateRegistrationCode();
          }
        } else {
          console.log('⏭️ Modal dismissed this session - showing button instead');
        }
      } else {
        // If endpoints exist, clear the dismissed flag so modal can show again if all endpoints are removed
        sessionStorage.removeItem('edr_onboarding_dismissed');
        console.log('📊 EDR Check: Found', endpointsData.length, 'endpoints');
      }

      // Set real threats data
      setThreats(threatsData || []);
      setActiveThreats(threatsData?.length || 0);

      // Set real system health from status
      if (statusData.health_metrics) {
        setSystemHealth(statusData.health_metrics);
      }

      // Set threat level from status
      if (statusData.threat_level) {
        setThreatLevel(statusData.threat_level);
      }

      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching EDR data:', err);
      setError(err.message || 'Failed to load EDR data');
      setLoading(false);
    }
  };

  // Add live fluctuation effect to health metrics
  useEffect(() => {
    if (!systemHealth || systemHealth.cpu === 0) return;

    const baseHealth = { ...systemHealth };
    
    // Create realistic fluctuation around real values
    const fluctuateInterval = setInterval(() => {
      setSystemHealth({
        cpu: Math.max(0, Math.min(100, baseHealth.cpu + (Math.random() - 0.5) * 3)),
        memory: Math.max(0, Math.min(100, baseHealth.memory + (Math.random() - 0.5) * 2)),
        disk: Math.max(0, Math.min(100, baseHealth.disk + (Math.random() - 0.5) * 1)),
        network: Math.max(0, Math.min(100, baseHealth.network + (Math.random() - 0.5) * 5))
      });
    }, 2000); // Update every 2 seconds

    return () => clearInterval(fluctuateInterval);
  }, [systemHealth?.cpu]); // Re-establish fluctuation when real data updates

  useEffect(() => {
    fetchEDRData();

    // Refresh data every 2 minutes (was 30 seconds - too fast for registration)
    const refreshInterval = setInterval(fetchEDRData, 120000);

    return () => clearInterval(refreshInterval);
  }, []);

  const handleClearAllEndpoints = async () => {
    if (!window.confirm('Are you sure you want to clear all endpoints? This will delete all endpoint data, events, and threats.')) {
      return;
    }

    try {
      setClearing(true);
      await edrService.clearAllEndpoints();
      
      // Clear the dismissed flag so modal shows again
      localStorage.removeItem('edr_onboarding_dismissed');
      
      // Refresh data after clearing
      await fetchEDRData();
      
      alert('All endpoints cleared successfully');
    } catch (error) {
      console.error('Error clearing endpoints:', error);
      alert('Failed to clear endpoints: ' + error.message);
    } finally {
      setClearing(false);
    }
  };

  const handleDismissModal = () => {
    setShowOnboardingModal(false);
    // Save dismissal to sessionStorage so modal doesn't show again during this session
    // But will show again on next login/browser session if still no endpoints
    sessionStorage.setItem('edr_onboarding_dismissed', 'true');
  };

  const generateRegistrationCode = async () => {
    try {
      setLoadingCode(true);
      const response = await edrService.generateRegistrationCode();
      setRegistrationCode(response.code);
    } catch (error) {
      console.error('Error generating registration code:', error);
      setRegistrationCode('ERROR');
    } finally {
      setLoadingCode(false);
    }
  };

  const copyRegistrationCode = () => {
    if (registrationCode) {
      navigator.clipboard.writeText(registrationCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const getThreatColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'protected': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'critical': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  if (loading && endpoints.length === 0) {
    return (
      <div className="edr-dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="animate-spin mx-auto mb-4" size={48} />
            <p>Loading EDR data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edr-dashboard">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="text-red-600 mr-3 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading EDR Data</h3>
              <p className="text-red-700">{error}</p>
              <button 
                onClick={fetchEDRData}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edr-dashboard">
      {/* Onboarding Modal */}
      {showOnboardingModal && (
        <div style={{position: 'fixed', top: '0', left: '0', right: '0', bottom: '0', width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: '99999'}}>
          <div className="rounded-lg shadow-2xl max-w-2xl w-full mx-4 p-8" style={{maxHeight: '90vh', overflowY: 'auto', background: 'rgba(6, 9, 15, 0.98)', border: '1px solid rgba(0, 212, 255, 0.3)', backdropFilter: 'blur(20px)'}}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <Shield style={{color: '#00d4ff'}} size={40} />
                <div>
                  <h2 className="text-2xl font-bold" style={{color: '#ffffff'}}>Get Started with EDR</h2>
                  <p style={{color: '#94a3b8'}} className="mt-1">Protect your devices with real-time monitoring</p>
                </div>
              </div>
              <button
                onClick={handleDismissModal}
                className="text-2xl leading-none" style={{color: '#94a3b8'}}
                onMouseEnter={(e) => e.target.style.color = '#ffffff'}
                onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
              >
                ×
              </button>
            </div>

            <div className="p-6 mb-6 rounded-lg" style={{background: 'rgba(0, 102, 255, 0.1)', borderLeft: '4px solid #0066ff'}}>
              <p style={{color: '#e2e8f0'}} className="mb-4">
                <strong>No endpoints are currently registered.</strong> Install the CyberGuard EDR agent on your devices to start monitoring for threats and suspicious activity.
              </p>
            </div>

            {/* Registration Code Display */}
            <div className="rounded-xl p-6 mb-6" style={{background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)', border: '1px solid rgba(0, 212, 255, 0.3)'}}>
              <p style={{color: '#e2e8f0'}} className="mb-3 font-medium text-center">Your Registration Code:</p>
              {loadingCode ? (
                <div className="text-center text-2xl" style={{color: '#94a3b8'}}>Loading...</div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <div className="rounded-lg p-4" style={{background: 'rgba(0, 212, 255, 0.1)', border: '2px solid #00d4ff'}}>
                    <div className="text-4xl font-bold tracking-wider font-mono" style={{color: '#00d4ff'}}>
                      {registrationCode || 'XXX-XXX'}
                    </div>
                  </div>
                  <button
                    onClick={copyRegistrationCode}
                    className="px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                    style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
                  >
                    {codeCopied ? (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012-2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              )}
              <p className="text-sm mt-3 text-center" style={{color: '#94a3b8'}}>This code is valid for 24 hours</p>
              <p className="text-xs mt-2 text-center" style={{color: '#64748b'}}>You'll need to paste this code when the installer prompts you</p>
            </div>

            <div className="space-y-6">
              {/* Simple 3-Step Process */}
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}>1</span>
                  <div className="flex-1">
                    <p className="font-medium mb-3" style={{color: '#ffffff'}}>Download the installer for your OS:</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={async () => {
                          const response = await fetch('https://cyberguard-backend-wa00.onrender.com/downloads/CyberGuard-Installer.zip');
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'CyberGuard-Installer.zip';
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors" style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
                      >
                        <span>🍎</span> Download for Mac
                      </button>
                      <button
                        onClick={async () => {
                          const response = await fetch('https://cyberguard-backend-wa00.onrender.com/downloads/CyberGuard-Installer.bat');
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'CyberGuard-Installer.bat';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors" style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
                      >
                        <span>🪟</span> Download for Windows
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}>2</span>
                  <div className="flex-1">
                    <p className="font-medium mb-3" style={{color: '#ffffff'}}>Run the installer:</p>

                    {/* Mac Instructions - Prominent */}
                    <div className="mb-4 rounded-lg p-4" style={{background: 'rgba(0, 102, 255, 0.1)', borderLeft: '4px solid #0066ff'}}>
                      <p className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-lg">🍎</span> Mac Installation:
                      </p>

                      <div className="bg-red-50 border-2 border-red-400 rounded p-4 mb-4">
                        <p className="text-sm font-bold text-red-900 mb-2">⚠️ Mac will block this app - Use ONE of these methods:</p>

                        <div className="space-y-3 mt-3">
                          <div className="bg-white rounded p-3 border border-red-200">
                            <p className="text-xs font-bold text-gray-900 mb-2">METHOD 1: System Settings (Recommended)</p>
                            <ol className="space-y-1 text-xs text-gray-700 ml-4 list-decimal">
                              <li>Double-click <strong>CyberGuard-Installer.zip</strong> to extract</li>
                              <li>Try to open <strong>CyberGuard Installer</strong> app (it will be blocked)</li>
                              <li>Open <strong>System Settings</strong> → <strong>Privacy & Security</strong></li>
                              <li>Scroll down - you'll see: "CyberGuard Installer was blocked"</li>
                              <li>Click <strong>"Open Anyway"</strong> button</li>
                              <li>Click <strong>"Open"</strong> in the confirmation dialog</li>
                            </ol>
                          </div>

                          <div className="bg-white rounded p-3 border border-red-200">
                            <p className="text-xs font-bold text-gray-900 mb-2">METHOD 2: Terminal Command (If Method 1 fails)</p>
                            <ol className="space-y-1 text-xs text-gray-700 ml-4 list-decimal">
                              <li>Open <strong>Terminal</strong> (in Applications → Utilities)</li>
                              <li>Type this command and press Enter:</li>
                            </ol>
                            <div className="mt-2 flex items-center gap-2">
                              <div className="flex-1 bg-gray-900 text-green-400 p-2 rounded font-mono text-xs overflow-x-auto">
                                xattr -cr ~/Downloads/CyberGuard-Installer.app
                              </div>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText('xattr -cr ~/Downloads/CyberGuard-Installer.app');
                                  const btn = document.getElementById('copy-terminal-btn');
                                  if (btn) {
                                    btn.textContent = 'Copied!';
                                    setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
                                  }
                                }}
                                id="copy-terminal-btn"
                                className="px-3 py-2 rounded text-xs font-semibold whitespace-nowrap"
                                style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
                              >
                                Copy
                              </button>
                            </div>
                            <p className="text-xs text-gray-600 mt-2 ml-4">Then double-click the app - it will open</p>
                          </div>

                          <div className="bg-white rounded p-3 border border-red-200">
                            <p className="text-xs font-bold text-gray-900 mb-2">METHOD 3: Right-Click Method</p>
                            <ol className="space-y-1 text-xs text-gray-700 ml-4 list-decimal">
                              <li>Extract the zip, find the app in Finder</li>
                              <li><strong>Right-click</strong> (or Control+click) the app</li>
                              <li>Select <strong>"Open"</strong> from the menu</li>
                              <li>Click <strong>"Open"</strong> in the dialog</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Windows Instructions */}
                    <div className="rounded-lg p-4" style={{background: 'rgba(0, 102, 255, 0.1)', borderLeft: '4px solid #0066ff'}}>
                      <p className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-lg">🪟</span> Windows Installation:
                      </p>
                      <ol className="space-y-2 text-sm text-gray-700 ml-6 list-decimal">
                        <li>Right-click <strong>CyberGuard-Installer.bat</strong></li>
                        <li>Select "Run as administrator"</li>
                        <li>Click "Yes" if prompted by User Account Control</li>
                        <li>Follow the on-screen prompts</li>
                      </ol>
                      <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-3">
                        <p className="text-xs font-bold text-gray-800 mb-1">💡 Tip:</p>
                        <p className="text-xs text-gray-700">
                          If Windows Defender blocks it, click "More info" → "Run anyway"
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  ✅ <strong>That's it!</strong> The agent will automatically install, register with your account, and start monitoring.
                  Refresh this page in about a minute to see your device appear.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleDismissModal}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Installation Instructions Modal */}
      {showInstallModal && (
        <div className="edr-modal-overlay" onClick={() => setShowInstallModal(false)}>
          <div className="edr-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <div className="edr-modal-header">
              <h3 style={{color: '#ffffff', fontSize: '1.25rem', fontWeight: 'bold'}}>Installation Instructions</h3>
              <button onClick={() => setShowInstallModal(false)} className="edr-modal-close">×</button>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg p-4" style={{background: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.4)'}}>
                <p className="font-medium flex items-center gap-2" style={{color: '#4ade80'}}>
                  <CheckCircle size={20} />
                  Download started! Check your Downloads folder.
                </p>
              </div>

              {selectedOS === 'mac' && (
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 1: Open Terminal</p>
                    <p className="text-sm mb-2" style={{color: '#94a3b8'}}>Press <kbd className="px-2 py-1 rounded text-xs" style={{background: 'rgba(255,255,255,0.1)', color: '#e2e8f0'}}>⌘ + Space</kbd> and type "Terminal"</p>
                  </div>

                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 2: Run this command:</p>
                    <div className="rounded-lg p-4" style={{background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
                      <code className="font-mono text-sm block" style={{color: '#00d4ff'}}>
                        bash ~/Downloads/cyberguard-install.sh
                      </code>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('bash ~/Downloads/cyberguard-install.sh');
                      }}
                      className="mt-2 text-sm" style={{color: '#00d4ff'}}
                    >
                      Copy command
                    </button>
                  </div>

                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 3: Enter your password</p>
                    <p className="text-sm" style={{color: '#94a3b8'}}>The installer needs admin access to set up the background service.</p>
                  </div>

                  <div className="rounded-lg p-4" style={{background: 'rgba(0, 102, 255, 0.15)', border: '1px solid rgba(0, 102, 255, 0.3)'}}>
                    <p className="text-sm" style={{color: '#93c5fd'}}>
                      The agent will automatically register and start monitoring. Refresh this page in about a minute to see your device!
                    </p>
                  </div>
                </div>
              )}

              {selectedOS === 'windows' && (
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 1: Open PowerShell as Administrator</p>
                    <p className="text-sm" style={{color: '#94a3b8'}}>Right-click Start menu → "Windows PowerShell (Admin)"</p>
                  </div>

                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 2: Navigate to Downloads:</p>
                    <div className="rounded-lg p-4" style={{background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
                      <code className="font-mono text-sm block" style={{color: '#00d4ff'}}>
                        cd $env:USERPROFILE\Downloads
                      </code>
                    </div>
                  </div>

                  <div>
                    <p className="font-semibold mb-2" style={{color: '#ffffff'}}>Step 3: Run the installer:</p>
                    <div className="rounded-lg p-4" style={{background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
                      <code className="font-mono text-sm block" style={{color: '#00d4ff'}}>
                        powershell -ExecutionPolicy Bypass -File .\cyberguard-install.ps1
                      </code>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('cd $env:USERPROFILE\\Downloads\npowershell -ExecutionPolicy Bypass -File .\\cyberguard-install.ps1');
                      }}
                      className="mt-2 text-sm" style={{color: '#00d4ff'}}
                    >
                      Copy both commands
                    </button>
                  </div>

                  <div className="rounded-lg p-4" style={{background: 'rgba(0, 102, 255, 0.15)', border: '1px solid rgba(0, 102, 255, 0.3)'}}>
                    <p className="text-sm" style={{color: '#93c5fd'}}>
                      The agent will automatically register and start monitoring. Refresh this page in about a minute to see your device!
                    </p>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowInstallModal(false)}
                className="w-full py-3 rounded-lg font-semibold transition-colors" style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 
  }

  return (
    <div className="edr-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{color: '#ffffff'}}>
            <Shield style={{color: '#00d4ff'}} size={32} />
            Endpoint Detection & Response
          </h2>
          <p className="mt-1" style={{color: '#94a3b8'}}>
            Real-time endpoint monitoring and threat detection
          </p>
        </div>
        <button
          onClick={fetchEDRData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50"
          style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
        >
          <RefreshCw className={loading ? 'animate-spin' : ''} size={18} />
          Refresh
        </button>
      </div>

      {/* Last Update Time */}
      {lastUpdate && (
        <div className="text-sm mb-4 flex items-center gap-2" style={{color: '#64748b'}}>
          <Clock size={16} />
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="rounded-lg p-4" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium" style={{color: '#e2e8f0'}}>CPU Usage</span>
            <Activity size={20} className="text-blue-500" />
          </div>
          <div className="text-2xl font-bold" style={{color: '#ffffff'}}>{systemHealth.cpu.toFixed(1)}%</div>
          <div className="w-full rounded-full h-2 mt-2" style={{background: 'rgba(255, 255, 255, 0.1)'}}>
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${systemHealth.cpu}%` }}
            />
          </div>
        </div>

        <div className="rounded-lg p-4" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium" style={{color: '#e2e8f0'}}>Memory</span>
            <TrendingUp size={20} className="text-green-500" />
          </div>
          <div className="text-2xl font-bold" style={{color: '#ffffff'}}>{systemHealth.memory.toFixed(1)}%</div>
          <div className="w-full rounded-full h-2 mt-2" style={{background: 'rgba(255, 255, 255, 0.1)'}}>
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${systemHealth.memory}%` }}
            />
          </div>
        </div>

        <div className="rounded-lg p-4" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium" style={{color: '#e2e8f0'}}>Disk Usage</span>
            <Monitor size={20} className="text-purple-500" />
          </div>
          <div className="text-2xl font-bold" style={{color: '#ffffff'}}>{systemHealth.disk.toFixed(1)}%</div>
          <div className="w-full rounded-full h-2 mt-2" style={{background: 'rgba(255, 255, 255, 0.1)'}}>
            <div
              className="bg-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${systemHealth.disk}%` }}
            />
          </div>
        </div>

        <div className="rounded-lg p-4" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium" style={{color: '#e2e8f0'}}>Network</span>
            <Globe size={20} className="text-orange-500" />
          </div>
          <div className="text-2xl font-bold" style={{color: '#ffffff'}}>{systemHealth.network.toFixed(1)}%</div>
          <div className="w-full rounded-full h-2 mt-2" style={{background: 'rgba(255, 255, 255, 0.1)'}}>
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${systemHealth.network}%` }}
            />
          </div>
        </div>
      </div>

      {/* Threat Level */}
      <div className="rounded-lg p-4 mb-6" style={{
        background: threatLevel === 'critical' ? 'rgba(239, 68, 68, 0.15)' :
                   threatLevel === 'high' ? 'rgba(249, 115, 22, 0.15)' :
                   threatLevel === 'medium' ? 'rgba(234, 179, 8, 0.15)' :
                   'rgba(34, 197, 94, 0.15)',
        border: `1px solid ${threatLevel === 'critical' ? 'rgba(239, 68, 68, 0.4)' :
                            threatLevel === 'high' ? 'rgba(249, 115, 22, 0.4)' :
                            threatLevel === 'medium' ? 'rgba(234, 179, 8, 0.4)' :
                            'rgba(34, 197, 94, 0.4)'}`
      }}>
        <div className="flex items-center gap-3">
          {threatLevel === 'low' ? (
            <CheckCircle className="text-green-400" size={24} />
          ) : (
            <AlertTriangle className="text-yellow-400" size={24} />
          )}
          <div>
            <div className="font-semibold uppercase" style={{color: '#ffffff'}}>
              Threat Level: {threatLevel}
            </div>
            <div className="text-sm" style={{color: '#94a3b8'}}>
              {endpoints.length} endpoints monitored • {threats.length} active threats
            </div>
          </div>
        </div>
      </div>

      {/* Endpoints List */}
      <div className="rounded-lg mb-6" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
        <div className="p-6 flex justify-between items-center" style={{borderBottom: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{color: '#ffffff'}}>
            <Monitor size={20} style={{color: '#00d4ff'}} />
            Monitored Endpoints ({endpoints.length})
          </h3>
          {endpoints.length > 0 && (
            <button
              onClick={handleClearAllEndpoints}
              disabled={clearing}
              className="px-3 py-1 text-sm rounded disabled:opacity-50"
              style={{background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)'}}
            >
              {clearing ? 'Resetting...' : 'Reset Endpoints'}
            </button>
          )}
        </div>
        <div className="p-6">
          {endpoints.length === 0 ? (
            <div className="text-center py-12">
              <Monitor size={64} className="mx-auto mb-6" style={{color: '#64748b'}} />
              <h3 className="text-xl font-semibold mb-2" style={{color: '#ffffff'}}>No Endpoints Registered</h3>
              <p className="mb-6" style={{color: '#94a3b8'}}>Install the CyberGuard EDR agent on your devices to start monitoring</p>

              <button
                onClick={() => {
                  setShowOnboardingModal(true);
                  generateRegistrationCode();
                }}
                className="mb-6 px-6 py-3 rounded-lg font-semibold shadow-lg"
                style={{background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)', color: '#ffffff'}}
              >
                Get Started - Deploy Agent
              </button>

              <div className="max-w-2xl mx-auto rounded-lg p-6 text-left" style={{background: 'rgba(0, 102, 255, 0.1)', border: '1px solid rgba(0, 102, 255, 0.3)'}}>
                <h4 className="font-semibold mb-3" style={{color: '#00d4ff'}}>📥 Get Started with EDR:</h4>
                <ol className="space-y-2 text-sm" style={{color: '#e2e8f0'}}>
                  <li className="flex items-start gap-2">
                    <span className="font-bold" style={{color: '#00d4ff'}}>1.</span>
                    <span>Download the agent for your operating system:</span>
                  </li>
                  <div className="ml-6 space-y-2 mt-2">
                    <a href="/edr-agent/install.sh" download className="inline-block px-4 py-2 rounded mr-2" style={{background: 'rgba(255, 255, 255, 0.1)', color: '#ffffff', border: '1px solid rgba(255, 255, 255, 0.2)'}}>
                      <span className="mr-2">🐧</span> Linux / macOS
                    </a>
                    <button disabled className="inline-block px-4 py-2 rounded cursor-not-allowed" style={{background: 'rgba(255, 255, 255, 0.05)', color: '#64748b'}}>
                      <span className="mr-2">🪟</span> Windows (Coming Soon)
                    </button>
                  </div>
                  <li className="flex items-start gap-2 mt-4">
                    <span className="font-bold" style={{color: '#00d4ff'}}>2.</span>
                    <span>Run the installer: <code className="px-2 py-1 rounded text-xs" style={{background: 'rgba(0, 0, 0, 0.3)'}}>bash install.sh</code></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold" style={{color: '#00d4ff'}}>3.</span>
                    <span>The agent will automatically register and start reporting</span>
                  </li>
                </ol>
                <div className="mt-4 pt-4" style={{borderTop: '1px solid rgba(0, 212, 255, 0.2)'}}>
                  <p className="text-xs" style={{color: '#94a3b8'}}>
                    💡 <strong>Note:</strong> The agent runs as a background service and reports system health, processes, and security events in real-time.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {endpoints.map((endpoint) => (
                <div key={endpoint.id} className="rounded-lg p-4 transition-colors" style={{background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(0, 212, 255, 0.15)'}}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Monitor size={32} style={{color: '#00d4ff'}} />
                      <div>
                        <div className="font-semibold" style={{color: '#ffffff'}}>{endpoint.hostname}</div>
                        <div className="text-sm" style={{color: '#94a3b8'}}>
                          {endpoint.os_type} {endpoint.os_version} • {endpoint.ip_address}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className={`flex items-center gap-2 ${getStatusColor(endpoint.status)}`}>
                        {endpoint.status === 'protected' ? (
                          <Shield size={20} />
                        ) : (
                          <AlertTriangle size={20} />
                        )}
                        <span className="font-medium capitalize">{endpoint.status}</span>
                      </div>
                      <div className="text-sm" style={{color: '#64748b'}}>
                        {new Date(endpoint.last_seen).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Threats List */}
      <div className="rounded-lg" style={{background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0, 212, 255, 0.2)'}}>
        <div className="p-6" style={{borderBottom: '1px solid rgba(0, 212, 255, 0.2)'}}>
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{color: '#ffffff'}}>
            <AlertTriangle size={20} style={{color: '#f87171'}} />
            Detected Threats ({threats.length})
          </h3>
        </div>
        <div className="p-6">
          {threats.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
              <p className="text-green-400 font-semibold">No threats detected</p>
              <p className="text-sm mt-2" style={{color: '#94a3b8'}}>All endpoints are secure</p>
            </div>
          ) : (
            <div className="space-y-4">
              {threats.map((threat) => (
                <div key={threat.id} className="p-4 rounded" style={{background: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid #ef4444'}}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getThreatColor(threat.severity)} text-white`}>
                          {threat.severity}
                        </span>
                        <span className="font-semibold" style={{color: '#ffffff'}}>{threat.threat_type}</span>
                      </div>
                      <p style={{color: '#e2e8f0'}}>{threat.description}</p>
                      <div className="text-sm mt-2" style={{color: '#94a3b8'}}>
                        Endpoint: {threat.endpoint_id} • {new Date(threat.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="ml-4">
                      <Zap size={20} className="text-red-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EndpointDetectionResponse;
